import { lazy, Suspense } from 'react';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Router context shared by every route.
 *
 * The QueryClient is threaded through the router so loaders can
 * prefetch into the same cache the components read from. See
 * src/main.tsx where the context is constructed and
 * apps/client/docs/overview.md §"How the client consumes the API"
 * for the broader server-state pattern.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

/**
 * Devtools are gated on `import.meta.env.DEV`, which Vite replaces at
 * build time with a literal boolean. The production branch resolves to
 * a no-op component, the dynamic import never enters the production
 * graph, and the @tanstack devtools packages are tree-shaken out of
 * the prod bundle.
 */
const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null;

function RootComponent() {
  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <TanStackRouterDevtools />
        <ReactQueryDevtools buttonPosition="bottom-left" />
      </Suspense>
    </>
  );
}
